import { useCallback, useState } from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import type { ClientePerfil } from '../hooks/useAdminClientePerfil';

const COLORS = {
  cream: '#FAF8F5',
  black: '#1A1A1A',
  sage: '#8BA888',
  sageLight: '#E8F0E7',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  white: '#FFFFFF',
  redLight: '#FEF2F2',
  redText: '#991B1B',
  amberLight: '#FFFBEB',
  amberText: '#92400E',
  skyLight: '#F0F9FF',
  skyText: '#075985',
  stoneLight: '#FAFAF9',
  stoneText: '#57534E',
};

const FONTS = {
  serif: 'Times-Roman',
  sans: 'Helvetica',
  sansBold: 'Helvetica-Bold',
  sansOblique: 'Helvetica-Oblique',
};

const styles = StyleSheet.create({
  page: {
    padding: 40,
    backgroundColor: COLORS.cream,
    fontFamily: FONTS.sans,
  },
  header: {
    marginBottom: 24,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray200,
    paddingBottom: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
  },
  title: {
    fontSize: 24,
    fontFamily: FONTS.serif,
    color: COLORS.black,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 11,
    color: COLORS.gray400,
    fontFamily: FONTS.sans,
  },
  date: {
    fontSize: 10,
    color: COLORS.gray400,
    fontFamily: FONTS.sans,
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.serif,
    color: COLORS.black,
    marginBottom: 12,
    marginTop: 20,
  },
  sectionLabel: {
    fontSize: 9,
    color: COLORS.gray400,
    fontFamily: FONTS.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  bodyText: {
    fontSize: 11,
    color: COLORS.gray600,
    lineHeight: 1.6,
    fontFamily: FONTS.sans,
  },
  noteText: {
    fontSize: 11,
    color: COLORS.gray600,
    lineHeight: 1.6,
    fontFamily: FONTS.sansOblique,
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: COLORS.gray200,
  },
  cardRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  tag: {
    backgroundColor: COLORS.sageLight,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginBottom: 4,
  },
  tagText: {
    fontSize: 10,
    color: COLORS.black,
    fontFamily: FONTS.sans,
  },
  tagRed: {
    backgroundColor: COLORS.redLight,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginBottom: 4,
  },
  tagRedText: {
    fontSize: 10,
    color: COLORS.redText,
    fontFamily: FONTS.sans,
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: COLORS.gray300,
  },
  colorItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: COLORS.cream,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 4,
  },
  colorLabel: {
    fontSize: 10,
    color: COLORS.black,
    fontFamily: FONTS.sans,
  },
  measureCard: {
    backgroundColor: COLORS.cream,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    minWidth: 100,
    flex: 1,
  },
  measureLabel: {
    fontSize: 8,
    color: COLORS.gray400,
    fontFamily: FONTS.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  measureValue: {
    fontSize: 14,
    color: COLORS.black,
    fontFamily: FONTS.sansBold,
  },
  brandCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: COLORS.cream,
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    minWidth: 120,
    flex: 1,
  },
  brandInitial: {
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: COLORS.black,
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandInitialText: {
    fontSize: 11,
    color: COLORS.white,
    fontFamily: FONTS.sansBold,
  },
  brandName: {
    fontSize: 10,
    color: COLORS.black,
    fontFamily: FONTS.sans,
  },
  brandSize: {
    fontSize: 11,
    color: COLORS.sage,
    fontFamily: FONTS.sansBold,
  },
  footer: {
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: COLORS.gray200,
    paddingTop: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerText: {
    fontSize: 9,
    color: COLORS.gray400,
    fontFamily: FONTS.sans,
  },
  accentBox: {
    backgroundColor: COLORS.sageLight,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  accentBoxText: {
    fontSize: 11,
    color: COLORS.black,
    lineHeight: 1.5,
    fontFamily: FONTS.sans,
  },
  warningBox: {
    backgroundColor: COLORS.redLight,
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  warningBoxText: {
    fontSize: 11,
    color: COLORS.redText,
    lineHeight: 1.5,
    fontFamily: FONTS.sans,
  },
  divider: {
    height: 1,
    backgroundColor: COLORS.gray200,
    marginVertical: 12,
  },
  row: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  sectionWrapper: {
    marginTop: 0,
    marginBottom: 0,
  },
  twoColCard: {
    flex: 1,
    minWidth: 200,
  },
  clienteNameText: {
    fontSize: 18,
    fontFamily: FONTS.serif,
    color: COLORS.black,
  },
});

function formatDate(): string {
  return new Date().toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function getTonoPielColor(tono?: string | null): { bg: string; text: string } {
  const t = tono?.toLowerCase() ?? '';
  if (t.includes('cálido') || t.includes('calido') || t.includes('warm')) {
    return { bg: COLORS.amberLight, text: COLORS.amberText };
  }
  if (t.includes('frío') || t.includes('frio') || t.includes('cool')) {
    return { bg: COLORS.skyLight, text: COLORS.skyText };
  }
  if (t.includes('neutro') || t.includes('neutral')) {
    return { bg: COLORS.stoneLight, text: COLORS.stoneText };
  }
  return { bg: COLORS.gray200, text: COLORS.gray500 };
}

function getTonoPielDesc(tono?: string | null): string {
  const t = tono?.toLowerCase() ?? '';
  if (t.includes('cálido') || t.includes('calido')) {
    return 'Tu tono de piel cálido se beneficia de colores dorados, terracotas, verdes bosque y marfil. Evita los plateados puros.';
  }
  if (t.includes('frío') || t.includes('frio')) {
    return 'Tu tono de piel frío luce mejor con colores plateados, azules, rosas pálidos y grises perla. Evita los dorados intensos.';
  }
  if (t.includes('neutro')) {
    return 'Tu tono de piel neutro es versátil: tanto colores cálidos como fríos te favorecen. Experimenta con ambas familias.';
  }
  return 'El tono de piel determina qué colores te favorecen más. Anthony ha evaluado el tuyo para crear una paleta personalizada.';
}

function getDotColor(color: string): string {
  const c = color.toLowerCase();
  if (c.includes('negro')) return '#1a1a1a';
  if (c.includes('blanco')) return '#f5f5f5';
  if (c.includes('gris')) return '#9ca3af';
  if (c.includes('beige') || c.includes('crudo') || c.includes('marfil')) return '#f5f5dc';
  if (c.includes('rojo') || c.includes('burdeos')) return '#dc2626';
  if (c.includes('azul') || c.includes('marino')) return '#2563eb';
  if (c.includes('verde') || c.includes('bosque') || c.includes('sage')) return '#16a34a';
  if (c.includes('rosa') || c.includes('palido')) return '#f472b6';
  if (c.includes('amarillo') || c.includes('oro')) return '#eab308';
  if (c.includes('naranja') || c.includes('terracota')) return '#ea580c';
  if (c.includes('morado') || c.includes('lila')) return '#9333ea';
  if (c.includes('cobre') || c.includes('bronze')) return '#b45309';
  if (c.includes('plata') || c.includes('perla')) return '#e5e7eb';
  return '#e5e7eb';
}

interface PerfilPDFProps {
  perfil: ClientePerfil;
  nombreCliente: string;
}

export function PerfilEstilisticoPDF({ perfil, nombreCliente }: PerfilPDFProps) {
  const tonoStyle = getTonoPielColor(perfil.tono_piel);
  const medidas = perfil.medidas_corporales;
  const hasMeasures = medidas && (
    medidas.altura || medidas.talla || medidas.contorno_pecho || medidas.contorno_cintura ||
    medidas.contorno_cadera || medidas.contorno_brazo || medidas.largo_pierna
  );
  const hasPaleta = perfil.paleta_colores && perfil.paleta_colores.length > 0;
  const hasOcasiones = perfil.ocasiones_preferidas && perfil.ocasiones_preferidas.length > 0;
  const hasEvitar = perfil.prendas_a_evitar && perfil.prendas_a_evitar.length > 0;
  const hasTallas = medidas?.tallas_por_marca && Object.keys(medidas.tallas_por_marca).length > 0;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>Perfil Estilístico</Text>
            <Text style={styles.subtitle}>Preparado por Anthony Sánchez Couture</Text>
          </View>
          <Text style={styles.date}>{formatDate()}</Text>
        </View>

        {/* Client name */}
        <View style={styles.card}>
          <Text style={styles.sectionLabel}>Cliente</Text>
          <Text style={styles.clienteNameText}>{nombreCliente}</Text>
        </View>

        {/* Paleta de colores */}
        {hasPaleta && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Paleta de colores recomendada</Text>
            <View style={styles.card}>
              <View style={styles.row}>
                {perfil.paleta_colores!.map((color) => (
                  <View key={color} style={styles.colorItem}>
                    <View style={{ ...styles.colorDot, backgroundColor: getDotColor(color) }} />
                    <Text style={styles.colorLabel}>{color}</Text>
                  </View>
                ))}
              </View>
              <View style={styles.divider} />
              <Text style={styles.bodyText}>
                Estos son los colores que Anthony ha seleccionado especialmente para ti. Úsalos como guía al crear looks, comprar nuevas prendas o elegir accesorios.
              </Text>
            </View>
          </View>
        )}

        {/* Estilo personal + Tono de piel */}
        <View style={styles.cardRow}>
          {perfil.estilo_personal && (
            <View style={{ ...styles.card, ...styles.twoColCard }}>
              <Text style={styles.sectionLabel}>Estilo personal</Text>
              <Text style={styles.bodyText}>{perfil.estilo_personal}</Text>
            </View>
          )}
          {perfil.tono_piel && (
            <View style={{
              ...styles.card,
              ...styles.twoColCard,
              backgroundColor: tonoStyle.bg,
              borderColor: tonoStyle.bg,
            }}>
              <Text style={{ ...styles.sectionLabel, color: tonoStyle.text }}>Tono de piel</Text>
              <Text style={{ ...styles.bodyText, color: tonoStyle.text, fontFamily: FONTS.sansBold }}>
                {perfil.tono_piel}
              </Text>
              <Text style={{ ...styles.bodyText, color: tonoStyle.text, fontSize: 10, marginTop: 4 }}>
                {getTonoPielDesc(perfil.tono_piel)}
              </Text>
            </View>
          )}
        </View>

        {/* Notas del estilista */}
        {perfil.notas_estilista && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Notas de Anthony</Text>
            <View style={styles.accentBox}>
              <Text style={styles.accentBoxText}>{perfil.notas_estilista}</Text>
            </View>
          </View>
        )}

        {/* Ocasiones preferidas */}
        {hasOcasiones && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Ocasiones prioritarias</Text>
            <View style={styles.card}>
              <View style={styles.row}>
                {perfil.ocasiones_preferidas!.map((ocasion) => (
                  <View key={ocasion} style={styles.tag}>
                    <Text style={styles.tagText}>{ocasion}</Text>
                  </View>
                ))}
              </View>
              <Text style={{ ...styles.bodyText, marginTop: 8, fontSize: 10 }}>
                Anthony ha definido estas ocasiones como las prioritarias en tu asesoría.
              </Text>
            </View>
          </View>
        )}

        {/* Prendas a evitar */}
        {hasEvitar && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Prendas a evitar</Text>
            <View style={styles.warningBox}>
              <View style={styles.row}>
                {perfil.prendas_a_evitar!.map((prenda) => (
                  <View key={prenda} style={styles.tagRed}>
                    <Text style={styles.tagRedText}>{prenda}</Text>
                  </View>
                ))}
              </View>
              <Text style={{ ...styles.warningBoxText, marginTop: 8, fontSize: 10 }}>
                Estos tipos de prenda no favorecen tu silueta o no se alinean con tu estilo personal.
              </Text>
            </View>
          </View>
        )}

        {/* Medidas corporales */}
        {hasMeasures && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Medidas corporales</Text>
            <View style={styles.card}>
              <View style={styles.cardRow}>
                {medidas?.altura && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Altura</Text>
                    <Text style={styles.measureValue}>{medidas.altura}</Text>
                  </View>
                )}
                {medidas?.talla && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Talla habitual</Text>
                    <Text style={styles.measureValue}>{medidas.talla}</Text>
                  </View>
                )}
                {medidas?.contorno_pecho && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Pecho</Text>
                    <Text style={styles.measureValue}>{medidas.contorno_pecho}</Text>
                  </View>
                )}
                {medidas?.contorno_cintura && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Cintura</Text>
                    <Text style={styles.measureValue}>{medidas.contorno_cintura}</Text>
                  </View>
                )}
                {medidas?.contorno_cadera && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Cadera</Text>
                    <Text style={styles.measureValue}>{medidas.contorno_cadera}</Text>
                  </View>
                )}
                {medidas?.contorno_brazo && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Brazo</Text>
                    <Text style={styles.measureValue}>{medidas.contorno_brazo}</Text>
                  </View>
                )}
                {medidas?.largo_pierna && (
                  <View style={styles.measureCard}>
                    <Text style={styles.measureLabel}>Largo pierna</Text>
                    <Text style={styles.measureValue}>{medidas.largo_pierna}</Text>
                  </View>
                )}
              </View>
              {medidas?.notas_medidas && (
                <View>
                  <View style={styles.divider} />
                  <Text style={styles.noteText}>{medidas.notas_medidas}</Text>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Tallas por marca */}
        {hasTallas && (
          <View style={styles.sectionWrapper}>
            <Text style={styles.sectionTitle}>Tallas por marca</Text>
            <View style={styles.card}>
              <View style={styles.cardRow}>
                {Object.entries(medidas!.tallas_por_marca!).map(([marca, talla]) => (
                  <View key={marca} style={styles.brandCard}>
                    <View style={styles.brandInitial}>
                      <Text style={styles.brandInitialText}>{marca.charAt(0).toUpperCase()}</Text>
                    </View>
                    <View>
                      <Text style={styles.brandName}>{marca}</Text>
                      <Text style={styles.brandSize}>{talla}</Text>
                    </View>
                  </View>
                ))}
              </View>
              <Text style={{ ...styles.bodyText, marginTop: 8, fontSize: 10 }}>
                Anthony ha registrado tus tallas por marca para compras online con confianza.
              </Text>
            </View>
          </View>
        )}

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>anthonysanchezcouture.com</Text>
          <Text style={styles.footerText}>Asesoría de imagen personal</Text>
        </View>
      </Page>
    </Document>
  );
}

export function PerfilEstilisticoPDFDownload({
  perfil,
  nombreCliente,
  children,
}: PerfilPDFProps & { children: (props: { loading: boolean }) => React.ReactNode }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    if (!perfil || loading) return;
    setLoading(true);
    setError(null);
    try {
      const doc = (
        <PerfilEstilisticoPDF perfil={perfil} nombreCliente={nombreCliente} />
      );
      const blob = await pdf(doc).toBlob();
      const url = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = `perfil-estilistico-${nombreCliente.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      link.target = '_blank';
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error al generar el PDF');
      // eslint-disable-next-line no-console
      console.error('PDF generation error:', err);
    } finally {
      setLoading(false);
    }
  }, [perfil, nombreCliente, loading]);

  if (!perfil) return null;

  return (
    <div onClick={handleDownload} className="cursor-pointer inline-block">
      {children({ loading })}
      {error && (
        <span className="block text-red-500 text-xs mt-1">{error}</span>
      )}
    </div>
  );
}